import { useEffect, useState } from 'react';
import { useApi } from '@/api/client';
import type { RecipientCheckResult, ReportParameterValue, SubscriptionRecipients } from '@/types';

/**
 * Watches the recipients and parameters being edited and asks the API whether they belong together.
 *
 * Debounced, because it runs while someone is typing an address and half-typed addresses are not
 * worth a round trip. This drives the inline warning only — the same check runs inside the save, so
 * a stale, failed or skipped answer here cannot let a mismatch through.
 *
 * In its own module rather than beside the components it feeds: a file that exports both a hook and
 * a component loses React Fast Refresh.
 */
export function useRecipientCheck(
  recipients: SubscriptionRecipients,
  parameters: ReportParameterValue[],
  enabled = true
): RecipientCheckResult | null {
  const api = useApi();
  const [result, setResult] = useState<RecipientCheckResult | null>(null);

  // Serialised rather than compared by reference: both are rebuilt on every keystroke elsewhere on
  // the page, so an identity comparison would re-request on edits that changed neither.
  const key = JSON.stringify([recipients, parameters]);

  useEffect(() => {
    if (!enabled) { setResult(null); return; }

    let cancelled = false;
    const timer = setTimeout(() => {
      api.checkRecipients(recipients, parameters)
        .then(r => { if (!cancelled) setResult(r); })
        // A failed check is not a failed edit. The save-time check is the one that counts.
        .catch(() => { if (!cancelled) setResult(null); });
    }, 500);

    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, enabled]);

  return result;
}
